import type { ComponentProps } from 'react'
import { useIntl } from 'react-intl'
import FilterChip from 'LuxKit/Chips/FilterChip'
import TextButton from 'LuxKit/Button/TextButton'
import OfferSortControl from 'components/Common/OfferSortControl/OfferSortControl'
import LineFilterAltIcon from 'LuxKit/Icons/line/LineFilterAltIcon'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import useGlobalSearchURLHashFilter from 'hooks/GlobalSearch/useGlobalSearchURLHashFilter'
import FilterDropdown from './FilterDropdown'
import { connect } from 'react-redux'
import getOfferListKey from 'lib/offer/offerListKey'
import { queryKeyAmenities, queryKeyHolidayTypes, queryKeyInclusions, queryKeyPropertyFacilities } from 'constants/url'
import { isHotelSearchResultsPath } from 'components/Pages/HotelSearchPage/utils/hotelFilterSectionsV2'
import CustomerRatingDropdown from './CustomerRatingDropdown'
import InclusionsDropdown from './InclusionsDropdown'
import Carousel from 'LuxKit/Carousel/Carousel'
import { rem } from 'polished'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import zIndex from 'styles/tools/z-index'
import { useInView } from 'react-intersection-observer'
import type { OfferListSortOption } from 'constants/offerListFilters'
import type { FilterControlChangeHandler } from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'
import { useScreenSizeOnly, useScreenSizeUp } from '../../../../hooks/useScreenSize'
import { useIsMobileOrTablet } from 'hooks/useIsMobileOrTablet'

const HeaderContainer = styled.div`
  position: sticky;
  z-index: ${zIndex.searchSubMenu};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  width: 100%;
  top: 0;

  &.map-overlay {
    background: transparent;
    position: relative;
    width: auto;
    box-shadow: none;
    && [data-map-filter-trigger] {
      background: ${props => props.theme.palette.neutral.surface.normalBackground};
      border-color: ${props => props.theme.palette.neutral.surface.lightForeground};
      --chip-stroke-color: ${props => props.theme.palette.neutral.surface.lightForeground};
      box-shadow: inset 0 0 0 var(--chip-stroke-width, 1px) var(--chip-stroke-color),
        ${props => props.theme.shadow.bottom.small};

      &.selected {
        --chip-stroke-color: ${props => props.theme.palette.neutral.surface.normalContrast};
      }
    }

    [data-map-filter-dropdown] > div {
      border-radius: ${rem(12)};
      box-shadow: ${props => props.theme.shadow.bottom.small};
    }

    [data-map-filter-dropdown] label {
      min-height: ${rem(32)};
      padding-block: ${rem(4)};
      cursor: pointer;
    }
  }

  &.lifted {
    box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.05);
  }
`

const ControlsRow = styled.div`
  display: flex;
  gap: ${rem(8)};

  > * {
    flex: 1;
    justify-content: center;
  }

  ${mediaQueryUp.tablet} {
    > * {
      flex: 0 0 auto;
    }
  }
`

interface MappedStateProps {
  availableFilters: App.OfferListFilterOptions | undefined
  search: string
  pathname: string
}

type InheritedOfferSortControlProps = Pick<ComponentProps<typeof OfferSortControl>, 'onSortChange'>

interface Props extends InheritedOfferSortControlProps {
  mapOverlay?: boolean
  filters: App.OfferListFilters
  showSort?: boolean
  sortOptions?: Array<OfferListSortOption>
  overrideAvailableFilters?: App.OfferListFilterOptions
  onFilterChange: FilterControlChangeHandler
  excludeOfferTypesFromCount?: boolean
  /** @deprecated Prefer `filterChipSize="large"`. */
  largeFilterChipSize?: boolean
  filterChipSize?: ComponentProps<typeof FilterChip>['size']
  mapViewUrl?: string
}

function HotelExposedFiltersHeader(props: Props & MappedStateProps) {
  const {
    mapOverlay = false,
    availableFilters,
    overrideAvailableFilters,
    filters,
    search,
    pathname,
    showSort = false,
    sortOptions,
    onFilterChange,
    onSortChange,
    excludeOfferTypesFromCount = false,
    largeFilterChipSize = false,
    filterChipSize,
    mapViewUrl,
  } = props
  const intl = useIntl()

  const chipSize = filterChipSize ?? (largeFilterChipSize ? 'large' : 'medium')
  const filterOptions = overrideAvailableFilters ?? availableFilters
  const { toggleFilterPanel } = useGlobalSearchURLHashFilter()
  const isMobile = useScreenSizeOnly('mobile')
  const isMobileOrTablet = useIsMobileOrTablet()
  const isDesktop = useScreenSizeUp('largeDesktop')
  const showSortDropdown = !isMobile
  const showAmenitiesChip = !isHotelSearchResultsPath(pathname)

  const [toLiftRef, liftInView] = useInView()

  const offerTypesCount = excludeOfferTypesFromCount ? 0 : (filters.offerTypes?.length === 1 ? 1 : 0)
  const hasBedroomFilter = !!(filters.bedroomsGte || filters.bedroomsEq)
  const amenitiesCount = showAmenitiesChip ? (filters.amenities?.length ?? 0) : 0
  const filterCount = (filters.holidayTypes?.length ?? 0) + amenitiesCount + (filters.inclusions?.length ?? 0) +
    (filters.propertyFacilities?.length ?? 0) + (filters.roomAmenities?.length ?? 0) +
    (filters.bedTypes?.length ?? 0) + (hasBedroomFilter ? 1 : 0) +
    (filters?.priceLte ? 1 : 0) + (filters.locations?.length ?? 0) + (filters.customerRatingGte ? 1 : 0) + offerTypesCount

  const allFiltersText = intl.formatMessage({
    id: '1CpQrQpseRhWVRxZvWhW',
    defaultMessage: 'All filters',
  })

  const customerRatingLabel = intl.formatMessage({
    id: 'Fmg8oNNFsuvKEX3qm8R6',
    defaultMessage: 'Customer rating',
  })

  const inclusionsLabel = intl.formatMessage({
    id: 'xqxLhsHQEb9XU42gCgr9',
    defaultMessage: 'Inclusions',
  })

  const typeOfEscapeLabel = intl.formatMessage({
    id: '6zL0mfztDFpmoqBdhxpA',
    defaultMessage: 'Type of escape',
  })

  const amenitiesLabel = intl.formatMessage({
    id: 'SDrddjVHD4KqX763IouH',
    defaultMessage: 'Amenities',
  })

  const filterCountLabel = filterCount ? intl.formatMessage(
    {
      id: 'kP4ohcXYgX3_NhsaOJVg',
      defaultMessage: '({filterCount, number})',
    },
    { filterCount },
  ) : ''

  const filterAndSortText = intl.formatMessage({
    id: 'Kwa_NLK1C0MSWf1ZnCg5',
    defaultMessage: 'Filter & Sort',
  })

  const mapText = intl.formatMessage({
    id: 'dgZ0q4-mlU-8LQv1Cntf',
    defaultMessage: 'Map',
  })

  if (mapOverlay ? !isDesktop : isMobileOrTablet) {
    return (
      <>
        <div ref={toLiftRef} />
        <HeaderContainer className={mapOverlay ? 'map-overlay' : !liftInView ? 'lifted' : ''}>
          <ControlsRow>
            <TextButton
              data-map-filter-trigger={mapOverlay || undefined}
              kind="secondary"
              variant="default"
              size="small"
              shape="round"
              startIcon={<LineFilterAltIcon />}
              onClick={toggleFilterPanel}
            >
              {mapOverlay ? allFiltersText : filterAndSortText} {filterCountLabel}
            </TextButton>
            {mapViewUrl && (
              <TextButton
                kind="secondary"
                variant="default"
                size="small"
                shape="round"
                startIcon={<LineMapIcon />}
                to={mapViewUrl}
              >
                {mapText}
              </TextButton>
            )}
          </ControlsRow>
        </HeaderContainer>
      </>
    )
  }

  return (
    <>
      <div ref={toLiftRef} />
      <HeaderContainer className={mapOverlay ? 'map-overlay' : !liftInView ? 'lifted' : ''}>
        <Carousel gap={8} gutterStyle="overflow">
          <FilterChip
            data-map-filter-trigger={mapOverlay || undefined}
            size={chipSize}
            aria-label={mapOverlay ? `${allFiltersText} ${filterCountLabel}`.trim() : undefined}
            startIcon={<LineFilterAltIcon />}
            onClick={toggleFilterPanel}
            selected={filterCount > 0}
          >
            {allFiltersText} {filterCountLabel}
          </FilterChip>
          {showSort && !!sortOptions?.length && (
            <OfferSortControl
              sortOptions={sortOptions}
              roundedButton
              showSelectedHighlight
              showDropdown={showSortDropdown}
              filterChipSize={chipSize}
              onSortChange={onSortChange}
            />
          )}
          <>
            <CustomerRatingDropdown mapOverlay={mapOverlay} filterChipSize={chipSize} filters={filters} search={search} label={customerRatingLabel} onFilterChange={onFilterChange} />
            <InclusionsDropdown
              mapOverlay={mapOverlay}
              filterChipSize={chipSize}
              searchQueryKey={queryKeyInclusions}
              label={inclusionsLabel}
              availableFilters={filterOptions?.filters?.inclusions}
              selectedFilters={filters?.inclusions ?? []}
              search={search}
              onFilterChange={onFilterChange}
            />
            <FilterDropdown
              mapOverlay={mapOverlay}
              filterChipSize={chipSize}
              searchQueryKey={queryKeyHolidayTypes}
              label={typeOfEscapeLabel}
              availableFilters={filterOptions?.filters?.holidayTypes}
              filterOrder={filterOptions?.filterOrder?.holidayTypes}
              selectedFilters={filters?.holidayTypes ?? []}
              search={search}
              onFilterChange={onFilterChange}
            />
            {mapOverlay && <FilterDropdown
              mapOverlay
              filterChipSize={chipSize}
              searchQueryKey={queryKeyPropertyFacilities}
              label={amenitiesLabel}
              availableFilters={filterOptions?.filters?.propertyFacilities}
              filterOrder={filterOptions?.filterOrder?.propertyFacilities}
              selectedFilters={filters.propertyFacilities ?? []}
              search={search}
              onFilterChange={onFilterChange}
            />}
            {showAmenitiesChip && (
              <FilterDropdown
                mapOverlay={mapOverlay}
                filterChipSize={chipSize}
                searchQueryKey={queryKeyAmenities}
                label={amenitiesLabel}
                availableFilters={filterOptions?.filters?.amenities}
                filterOrder={filterOptions?.filterOrder?.amenities}
                selectedFilters={filters?.amenities ?? []}
                search={search}
                onFilterChange={onFilterChange}
              />
            )}
          </>
        </Carousel>
      </HeaderContainer>
    </>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>(
  (appState, ownProps): MappedStateProps => {
    const key = getOfferListKey(ownProps.filters)
    const availableFilters = appState.offer.offerListFilterOptions
    return {
      availableFilters: availableFilters[key],
      search: appState.router.location.search,
      pathname: appState.router.location.pathname,
    }
  },
)(HotelExposedFiltersHeader)
